import React from "react";
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Image, Video, CheckSquare, Square } from 'lucide-react';
import { StagedFile } from '@/types/assetGrouping';

interface StagedFileGridProps {
  files: StagedFile[];
  onToggleSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  selectedCount: number;
  totalCount: number;
}

export function StagedFileGrid({
  files,
  onToggleSelect,
  onRemove,
  onSelectAll,
  onDeselectAll,
  selectedCount,
  totalCount
}: StagedFileGridProps) {
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      {/* Header with selection controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">
            {selectedCount} of {totalCount} selected
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onSelectAll}>
            <CheckSquare className="h-4 w-4 mr-1" />
            Select All
          </Button>
          <Button variant="ghost" size="sm" onClick={onDeselectAll}>
            <Square className="h-4 w-4 mr-1" />
            Deselect All
          </Button>
        </div>
      </div>

      {/* File grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {files.map((file) => (
          <Card
            key={file.id}
            className={`relative group overflow-hidden ${
              file.selected ? 'ring-2 ring-primary' : ''
            }`}
          >
            {/* Checkbox */}
            <div className="absolute top-2 left-2 z-10">
              <Checkbox
                checked={file.selected}
                onCheckedChange={() => onToggleSelect(file.id)}
                className="bg-background/90 border-muted-foreground"
              />
            </div>

            {/* Remove button */}
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 z-10 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => onRemove(file.id)}
            >
              <X className="h-3 w-3" />
            </Button>

            {/* Preview */}
            <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden">
              {file.type === 'image' ? (
                <img
                  src={file.preview_url}
                  alt={file.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <video
                  src={file.preview_url}
                  className="w-full h-full object-cover"
                  muted
                />
              )}
            </div>

            {/* Info */}
            <div className="p-2 space-y-1">
              <div className="flex items-center gap-1">
                {file.type === 'image' ? (
                  <Image className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                ) : (
                  <Video className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                )}
                <span className="text-xs font-medium truncate" title={file.name}>
                  {file.name}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{file.dimensions}</span>
                <span>{formatSize(file.size)}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
